import axios from 'axios';
import { StreamWithChatRate } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({ baseURL: API_URL });

export interface PartyParticipant {
  id: string;
  nickname: string;
  isHost: boolean;
}

export interface PartyMessage {
  id: string;
  participantId: string;
  nickname: string;
  text: string;
  timestamp: number;
}

export interface PartyStream {
  id: string;
  user_login: string;
  user_name: string;
  game_name: string;
  title: string;
  thumbnail_url: string;
  viewer_count: number;
}

export interface Party {
  code: string;
  hostId: string;
  hostNickname: string;
  stream: PartyStream | null;
  participants: PartyParticipant[];
  messages: PartyMessage[];
  createdAt: number;
}

export interface JoinResult {
  code: string;
  participantId: string;
  isHost: boolean;
  party: Party;
}

function toPartyStream(stream: StreamWithChatRate | PartyStream | null): PartyStream | null {
  if (!stream) return null;
  return {
    id: String(stream.id || stream.user_login),
    user_login: stream.user_login,
    user_name: stream.user_name,
    game_name: (stream as any).game_name || '',
    title: (stream as any).title || '',
    thumbnail_url: (stream as any).thumbnail_url || '',
    viewer_count: (stream as any).viewer_count || 0,
  };
}

export async function createParty(nickname: string, stream: StreamWithChatRate | null): Promise<JoinResult> {
  const res = await api.post('/api/party/create', {
    nickname,
    stream: toPartyStream(stream),
  });
  return res.data;
}

export async function joinParty(code: string, nickname: string): Promise<JoinResult> {
  const res = await api.post(`/api/party/${encodeURIComponent(code)}/join`, { nickname });
  return res.data;
}

export async function getParty(code: string): Promise<Party> {
  const res = await api.get(`/api/party/${encodeURIComponent(code)}`);
  return res.data.party;
}

export async function sendPartyMessage(code: string, participantId: string, text: string): Promise<void> {
  await api.post(`/api/party/${encodeURIComponent(code)}/message`, { participantId, text });
}

export async function setPartyStream(code: string, participantId: string, stream: StreamWithChatRate | PartyStream): Promise<void> {
  await api.post(`/api/party/${encodeURIComponent(code)}/stream`, {
    participantId,
    stream: toPartyStream(stream),
  });
}

export async function partyHeartbeat(code: string, participantId: string): Promise<{ success: boolean; isHost: boolean }> {
  const res = await api.post(`/api/party/${encodeURIComponent(code)}/heartbeat`, { participantId });
  return res.data;
}

export async function leaveParty(code: string, participantId: string): Promise<void> {
  try {
    await api.post(`/api/party/${encodeURIComponent(code)}/leave`, { participantId });
  } catch {
    // ignore
  }
}

export type PartyEventHandlers = {
  onState?: (party: Party) => void;
  onStream?: (stream: PartyStream) => void;
  onMessage?: (message: PartyMessage) => void;
  onError?: () => void;
};

export function subscribeParty(code: string, handlers: PartyEventHandlers): () => void {
  const url = `${API_URL}/api/party/${encodeURIComponent(code)}/events`;
  const es = new EventSource(url);

  es.addEventListener('state', (ev) => {
    try { handlers.onState?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.addEventListener('stream', (ev) => {
    try { handlers.onStream?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.addEventListener('message', (ev) => {
    try { handlers.onMessage?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.onerror = () => { handlers.onError?.(); };

  return () => es.close();
}
